import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ToastrService from "../../services/toastService";
import Loader from "../loader/loader"
import { Logout, shouldLogout } from '../Common/Common'
import { FaPlus, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAddIntentMutation, useUpdateIntentMutation } from "./Api";

const AddIntent = ({
    mode = "add",
    onClose,
    onSave,
    data = null,
}) => {

    const [addIntentApi, { isLoading }] = useAddIntentMutation()
    const [updateIntentApi, { isLoading: updateIsLoading }] = useUpdateIntentMutation()

    const navigate = useNavigate()

    const validationSchema = Yup.object().shape({
        intent_name: Yup.string()
            .required('Intent Name is required')
            .matches(/^[A-Za-z\s]+$/, 'Enter valid intent name')
            .trim(),
        description: Yup.string()
            .required('Description is required')
            .trim(),
        // keywords: Yup.array()
        //     .of(Yup.string())
        //     .min(1, 'At least one keyword is required'),
        keywords: Yup.array()
            .of(
                Yup.string()
                    .trim()
                    .required("Keyword cannot be empty")
            )
            .min(1, "At least one keyword is required"),
        status: Yup.string()
            .required('Status is required')
            .trim(),
    });

    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            keywords: []
        },
        mode: 'all'
    });

    const keywords = watch("keywords") || [];
    const [keywordInput, setKeywordInput] = useState("");

    useEffect(() => {
        if (data) {
            reset({
                intent_name: data.intent_name,
                description: data.description,
                keywords: data.keywords || [],
                status: data.status,
            });
        }
    }, [])

    const addKeyword = () => {
        const trimmed = keywordInput.trim();

        if (!trimmed) return;

        // if (keywords.includes(trimmed)) return;

        // Duplicate check (case-insensitive)
        const isDuplicate = keywords.some(
            (kw) => kw.toLowerCase() === trimmed.toLowerCase()
        );

        if (isDuplicate) {
            ToastrService.error("Keyword already added!");
            setKeywordInput("");
            return;
        }

        setValue("keywords", [...keywords, trimmed]);
        setKeywordInput("");
    };

    const removeKeyword = (index) => {
        const updated = keywords.filter((_, i) => i !== index);
        setValue("keywords", updated);
    };


    const resetButton = () => {
        reset({
            intent_name: "",
            description: "",
            keywords: [],
            status: "",
        });
    };

    const onSubmit = async (payload) => {
        try {
            if (mode == 'add') {

                const values = {
                    intent_name: payload.intent_name,
                    description: payload.description,
                    keywords: payload.keywords,
                    status: payload.status,
                }

                const response = await addIntentApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }

            } else {

                const values = {
                    intent_name: payload.intent_name,
                    description: payload.description,
                    status: payload.status,
                    keywords: payload.keywords,
                    id: data.id
                }

                const response = await updateIntentApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }
            }
        } catch (err) {
            console.log(err, 'err')
        }
    }

    return (
        <div>
            {isLoading || updateIsLoading ? <Loader /> :
                <>
                    <div className="offcanvas offcanvas-end show" style={{ visibility: "visible" }} tabIndex="-1">

                        <div className="offcanvas-header px-4 py-3">

                            <div>
                                <h3 className="mb-1 drawer-header">
                                    {mode === "add" ? "Create New Intent" : "Manage Intent"}
                                </h3>
                                <small className="text-muted">{mode === "add" ? "Fill in the details below to create a KB Intent." : "Update the details below to Manage KB Intent"}</small>
                            </div>

                            <button
                                type="button"
                                className="btn-close text-reset"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>

                        </div>


                        <div className="offcanvas-body px-4 py-3">

                            <form autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Intent Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="intent_name"
                                        name='intent_name'
                                        placeholder='Enter the Intent name'
                                        {...register('intent_name')}
                                        className="form-control"

                                    />
                                    {errors.intent_name && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.intent_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        id="description"
                                        placeholder="Brief description of the Intent..."
                                        name='description'
                                        {...register('description')}
                                        className="description-input"
                                    />
                                    {errors.description && (
                                        <p className="mt-1" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.description.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Keywords <span className="text-danger">*</span>
                                    </label>

                                    <div className="d-flex gap-2">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter keyword (e.g. reset password)"
                                            value={keywordInput}
                                            onChange={(e) => setKeywordInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addKeyword();
                                                }
                                            }}
                                        />

                                        <button
                                            type="button"
                                            onClick={addKeyword}
                                            className="btn btn-primary d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "38px",
                                                height: "38px",
                                                padding: 0
                                            }}
                                            aria-label="Add keyword"
                                        >
                                            <FaPlus />
                                        </button>
                                    </div>

                                    {/* Chips UI */}
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {keywords.map((kw, index) => (
                                            <span
                                                key={index}
                                                // style={{
                                                //     padding: "6px 10px",
                                                //     borderRadius: "20px",
                                                //     background: "#e9ecef",
                                                //     display: "flex",
                                                //     alignItems: "center",
                                                //     gap: "8px"
                                                // }}
                                                className="keyword-chip"
                                            >
                                                {kw}

                                                <button
                                                    type="button"
                                                    onClick={() => removeKeyword(index)}
                                                    // style={{
                                                    //     border: "none",
                                                    //     background: "transparent",
                                                    //     cursor: "pointer",
                                                    //     display: "flex",
                                                    //     alignItems: "center"
                                                    // }}
                                                    className="keyword-chip-remove"
                                                    aria-label="Remove keyword"
                                                >
                                                    <FaTimes size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>

                                    {errors.keywords && (
                                        <p className="mt-2" style={{ color: "#d93025" }}>
                                            {errors.keywords.message}
                                        </p>
                                    )}
                                </div>


                                <div className="mb-3">
                                    <label className="form-label">
                                        Status <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        {...register('status')}
                                        className="custom-select"
                                    >
                                        <option value="">Select status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    {errors.status && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.status.message}
                                        </p>
                                    )}

                                </div>

                                <div className="d-flex gap-2 mt-4">
                                    <button type="submit" className="btn btn-primary">
                                        {mode === "add" ? "Create intent" : "Update intent"}
                                    </button>

                                    {mode === "add" && (
                                        <button type="button" className="btn btn-secondary" onClick={resetButton}>
                                            Reset
                                        </button>
                                    )}
                                </div>

                            </form>

                        </div>
                    </div>

                    {/* Backdrop */}
                    <div className="offcanvas-backdrop fade show" onClick={onClose}></div>

                </>
            }
        </div>

    );
};

export default AddIntent;